function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import { Card, Badge, Spinner, Avatar } from "flowbite-react";
import PropTypes from "prop-types";
import { format, formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ImPriceTag, ImClock2 } from 'react-icons/im';
import { TbBrandReddit } from 'react-icons/tb';
import { Config } from "../../app.config";
import { useSearchContext } from "../utils/Context";
import { gaEvent } from "../utils/Utils";
import { BlockquoteRegEx, LinkClasses, ThreadTypes } from "../utils/Constants";
import Help from "./Help";
import Reset from "./Reset";
import Share from "./Share";
import Error from "./Error";
import Suggestion from "./Suggestion";
import Announce from "./Announce";
var ResultItem = function ResultItem(props) {
  var _useSearchContext = useSearchContext(),
    options = _useSearchContext.options;
  var handleAuthorClick = function handleAuthorClick(event, comment, link) {
    gaEvent("author", {
      category: "Author",
      label: "author",
      value: comment.author,
      nonInteraction: true
    });
  };
  var handleResultClick = function handleResultClick(event, comment, link) {
    gaEvent("result", {
      category: "Result",
      label: "thread",
      value: comment.thread,
      nonInteraction: true
    });
    gaEvent("result", {
      category: "Result",
      label: "author",
      value: comment.author,
      nonInteraction: true
    });
  };
  var redditDomain = "https://".concat(options.oldReddit ? 'old' : 'www', ".reddit.com");
  var permalink;
  if (props.permalink) {
    permalink = props.permalink;
  } else {
    permalink = "/comments/".concat(props.link_id.split('_')[1], "/_/").concat(props.id, "/");
  }
  var commentLink = "".concat(redditDomain).concat(permalink, "?context=1");
  var threadBadge;
  if (props.thread) {
    threadBadge = /*#__PURE__*/React.createElement(Badge, {
      icon: ImPriceTag,
      color: ThreadTypes[props.thread].color,
      size: "xs",
      className: "pr-1.5"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sr-only"
    }, "Comment Thread:"), ThreadTypes[props.thread].name);
  }
  var commentFormattedDate = format(new Date(props.created_utc * 1000), "M/d/yy h:mm aaa");
  var commentPosted = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "inline md:hidden"
  }, formatDistanceToNow(new Date(props.created_utc * 1000), {
    addSuffix: false
  })), /*#__PURE__*/React.createElement("span", {
    className: "hidden md:inline"
  }, formatDistanceToNow(new Date(props.created_utc * 1000), {
    addSuffix: true
  })));
  if (options.showDate) {
    commentPosted = commentFormattedDate;
  }
  var subredditBadge = /*#__PURE__*/React.createElement(Badge, {
    icon: TbBrandReddit,
    color: "success",
    size: "xs",
    className: "pr-1.5"
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, "Subreddit:"), " ", props.subreddit);
  var postedBadge = /*#__PURE__*/React.createElement(Badge, {
    icon: ImClock2,
    color: "warning",
    size: "xs",
    className: "pr-1.5",
    title: commentFormattedDate
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, "Comment Posted:"), " ", commentPosted);
  var avatarSeed = Array.from(props.author || props.id || "").reduce(function (sum, _char) {
    return sum + _char.charCodeAt(0);
  }, 0);
  var authorAvatar = /*#__PURE__*/React.createElement(Avatar, {
    size: "xs",
    img: "https://www.redditstatic.com/avatars/defaults/v2/avatar_default_".concat(avatarSeed % 8, ".png")
  });
  return /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap items-center justify-between -mx-2 md:mx-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap items-center gap-3"
  }, authorAvatar, /*#__PURE__*/React.createElement("a", {
    href: "".concat(redditDomain, "/user/").concat(props.author),
    onClick: function onClick(e) {
      return handleAuthorClick(e, props, permalink);
    },
    className: "font-semibold text-base no-underline md:text-lg ".concat(LinkClasses),
    target: "_blank"
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, "Comment Author:"), " ", props.author)), threadBadge), /*#__PURE__*/React.createElement("div", {
    className: "-mx-2 md:mx-0"
  }, /*#__PURE__*/React.createElement("a", {
    href: commentLink,
    onClick: function onClick(e) {
      return handleResultClick(e, props, permalink);
    },
    className: "block text-sm leading-snug reddit-comment text-ellipsis overflow-hidden no-underline",
    target: "_blank"
  }, /*#__PURE__*/React.createElement(ReactMarkdown, {
    disallowedElements: ['a'],
    remarkPlugins: [remarkGfm],
    unwrapDisallowed: true
  }, props.body.replace(BlockquoteRegEx, "\n\n>")))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap justify-".concat(subredditBadge ? 'between' : 'end', " items-center -mx-2 md:mx-0")
  }, subredditBadge, postedBadge));
};
ResultItem.propTypes = {
  author: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  created_utc: PropTypes.number.isRequired,
  link_id: PropTypes.string,
  id: PropTypes.string,
  permalink: PropTypes.string,
  thread: PropTypes.string,
  subreddit: PropTypes.string,
  resultNumber: PropTypes.number
};
var Results = function Results() {
  var _useSearchContext2 = useSearchContext(),
    comments = _useSearchContext2.comments,
    allData = _useSearchContext2.allData,
    filteredCount = _useSearchContext2.filteredCount,
    totalCount = _useSearchContext2.totalCount,
    searching = _useSearchContext2.searching,
    options = _useSearchContext2.options,
    searched = _useSearchContext2.searched,
    error = _useSearchContext2.error,
    query = _useSearchContext2.query;
  if (error) {
    return /*#__PURE__*/React.createElement(Error, null);
  }
  if (searching) {
    return /*#__PURE__*/React.createElement("div", {
      className: "p-8 w-full"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col text-center"
    }, /*#__PURE__*/React.createElement(Spinner, {
      "aria-label": "Searching...",
      size: "xl"
    })));
  }
  if (!searched) {
    return /*#__PURE__*/React.createElement("div", {
      className: "md:pt-6 px-4 md:px-6 lg:px-8 pb-6 w-full"
    }, /*#__PURE__*/React.createElement(Announce, null), /*#__PURE__*/React.createElement(Help, null));
  } else {
    var resultsContent;
    if (allData.length === 0 && comments.length === 0) {
      resultsContent = /*#__PURE__*/React.createElement("div", {
        className: "p-8 w-full"
      }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement("div", {
        className: "font-semibold text-lg text-center"
      }, "No Results Found")));
    } else {
      if (comments.length !== 0) {
        resultsContent = [comments.map(function (comment, index) {
          return /*#__PURE__*/React.createElement(ResultItem, _extends({
            key: index
          }, comment, {
            resultNumber: index + 1
          }));
        }), /*#__PURE__*/React.createElement("div", {
          key: 9999,
          className: "font-semibold text-center my-4"
        }, "End of Results")];
      }
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      id: "results-top-bar",
      className: "flex justify-between items-center px-4 py-3 border-b border-t lg:border-t-0 border-gray-200 dark:border-gray-700 bg-blue-100 dark:bg-slate-800"
    }, /*#__PURE__*/React.createElement("div", {
      id: "results-title",
      "aria-live": "polite",
      "aria-atomic": "true",
      className: "font-semibold text-lg text-gray-700 dark:text-gray-50"
    }, "Showing ", filteredCount < totalCount ? "".concat(filteredCount, " of ") : '', totalCount, " result", totalCount === 1 ? '' : 's'), /*#__PURE__*/React.createElement("div", {
      className: "flex flex-wrap items-center gap-3 md:gap-4"
    }, /*#__PURE__*/React.createElement(Share, null), /*#__PURE__*/React.createElement(Reset, null))), /*#__PURE__*/React.createElement("main", {
      id: "results-list",
      role: "region",
      "aria-label": "Search Results",
      className: "flex-1 overflow-y-scroll py-4 px-2 md:px-6 lg:px-10 xl:px-12 space-y-6"
    }, /*#__PURE__*/React.createElement(Suggestion, {
      query: query
    }), /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col gap-4"
    }, resultsContent)), /*#__PURE__*/React.createElement("div", {
      id: "results-footer",
      className: "flex justify-between items-center px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-center text-xs bg-blue-100 dark:bg-slate-800"
    }, /*#__PURE__*/React.createElement("div", null, "Maintained by ", /*#__PURE__*/React.createElement("a", {
      href: "https://".concat(options.oldReddit ? 'old' : 'www', ".reddit.com/user/").concat(Config.author),
      target: "_blank",
      className: LinkClasses
    }, Config.author)), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("a", {
      href: "https://".concat(options.oldReddit ? 'old' : 'www', ".reddit.com/message/compose/?to=").concat(Config.author, "&subject=").concat(Config.name.replace(/\s+/g, '+')),
      target: "_blank",
      className: LinkClasses
    }, "PM with comments, suggestions, issues"))));
  }
};
export default Results;